import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Suspense, lazy, useEffect, useRef, useContext } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Layout from './components/Layout/Layout';
import { AuthContext } from './context/AuthContext';
import Loader from './utils/Loader';

// ───── Lazy imports ─────────────────────────────────────────────────────────────
const Login = lazy(() => import('./pages/Login'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));

// Identity Center Auth Pages (hosted login UI)
const ICLogin = lazy(() => import('./pages/auth/ICLogin'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const OAuthError = lazy(() => import('./pages/auth/OAuthError'));

// Dashboard
const Dashboard = lazy(() => import('./pages/Dashboard'));

// Organizations
const OrgList = lazy(() => import('./pages/organizations/OrgList'));
const CreateOrganization = lazy(() => import('./pages/organizations/CreateOrganization'));
const PendingOrganizations = lazy(() => import('./pages/organizations/PendingOrganizations'));

// Users
const UserList = lazy(() => import('./pages/users/UserList'));

// Templates
const AdvancedLOBTemplateManager = lazy(() => import('./pages/templates/AdvancedLOBTemplateManager'));
const GlobalFieldOptions = lazy(() => import('./pages/templates/GlobalFieldOptions'));
const TemplateAssignment = lazy(() => import('./pages/templates/TemplateAssignment'));

// Template Management (Master Library)
const TemplateList = lazy(() => import('./pages/templates/TemplateList'));
const TemplateForm = lazy(() => import('./pages/templates/TemplateForm'));
const TemplateDetail = lazy(() => import('./pages/templates/TemplateDetail'));

// Billing
const PlanManager = lazy(() => import('./pages/billing/PlanManager'));
const ServiceModules = lazy(() => import('./pages/billing/ServiceModules'));
const SubscriptionManager = lazy(() => import('./pages/billing/SubscriptionManager'));
const InvoiceManager = lazy(() => import('./pages/billing/InvoiceManager'));
const PaymentGateway = lazy(() => import('./pages/billing/PaymentGateway'));
const RevenueAnalytics = lazy(() => import('./pages/billing/RevenueAnalytics'));
const CompanySettings = lazy(() => import('./pages/billing/CompanySettings'));
const PromoCodes = lazy(() => import('./pages/billing/PromoCodes'));

// Team
const TeamManagement = lazy(() => import('./pages/team/TeamManagement'));

// System
const ServerMonitor = lazy(() => import('./pages/system/ServerMonitor'));
const AsteriskCluster = lazy(() => import('./pages/system/AsteriskCluster'));
const ReviewManagement = lazy(() => import('./pages/system/ReviewManagement'));

// Platform
const PlatformDashboard = lazy(() => import('./pages/platform/PlatformDashboard'));
const OrganizationDetail = lazy(() => import('./pages/platform/OrganizationDetail'));
const UserManagement = lazy(() => import('./pages/platform/UserManagement'));
const AuditLogViewer = lazy(() => import('./pages/platform/AuditLogViewer'));

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasToken, loading, authChecked, setCurrentPath } = useContext(AuthContext);

  const navigationRef = useRef(false);
  const userRole = sessionStorage.getItem('user_role');
  const isLoggedIn = hasToken && user;

  useEffect(() => {
    setCurrentPath(location.pathname);

    if (isLoggedIn && location.pathname === '/' && !navigationRef.current) {
      navigationRef.current = true;
      navigate('/dashboard', { replace: true });
    }

    if (!isLoggedIn) {
      navigationRef.current = false;
    }
  }, [isLoggedIn, location.pathname, navigate, setCurrentPath]);

  if (!authChecked || loading) {
    return <Loader />;
  }

  return (
    <>
      <ToastContainer
        position="bottom-right"
        autoClose={3500}
        hideProgressBar
        closeOnClick
        pauseOnHover
        closeButton={false}
        toastClassName="!bg-white dark:!bg-gray-800 !text-primary-black dark:!text-white !rounded-xl !shadow-lg !border !border-stroke dark:!border-gray-700 !px-4 !py-3 !text-sm !font-medium"
        bodyClassName="!p-0"
      />
      <Suspense fallback={<Loader />}>
        <Routes>
          {/* Auth callback */}
          <Route path="/auth/callback" element={<AuthCallback />} />

          {/* Identity Center hosted auth pages */}
          <Route path="/auth/login" element={<ICLogin />} />
          <Route path="/auth/forgot-password" element={<ForgotPassword />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          <Route path="/auth/setup-password" element={<ResetPassword />} />
          <Route path="/auth/error" element={<OAuthError />} />

          {!isLoggedIn ? (
            <>
              <Route path="/" element={<Login />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          ) : (
            <Route path="/" element={<Layout />}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />

              {/* Organizations */}
              <Route path="organizations" element={<OrgList />} />
              <Route path="organizations/create" element={<CreateOrganization />} />
              <Route path="organizations/pending" element={<PendingOrganizations />} />

              {/* Users */}
              <Route path="users" element={<UserList />} />

              {/* Team */}
              <Route path="team" element={<TeamManagement />} />

              {/* Templates — Master Library Management */}
              <Route path="templates" element={<TemplateList />} />
              <Route path="templates/create" element={<TemplateForm />} />
              <Route path="templates/:id" element={<TemplateDetail />} />
              <Route path="templates/:id/edit" element={<TemplateForm />} />

              {/* Templates — Legacy */}
              <Route path="templates/lob-manager" element={<AdvancedLOBTemplateManager />} />
              <Route path="field-options" element={<GlobalFieldOptions />} />
              <Route path="template-assignment" element={<TemplateAssignment />} />

              {/* Billing */}
              <Route path="billing/plans" element={<PlanManager />} />
              <Route path="billing/services" element={<ServiceModules />} />
              <Route path="billing/subscriptions" element={<SubscriptionManager />} />
              <Route path="billing/invoices" element={<InvoiceManager />} />
              <Route path="billing/payment-gateway" element={<PaymentGateway />} />
              <Route path="billing/overview" element={<RevenueAnalytics />} />
              <Route path="billing/company-settings" element={<CompanySettings />} />
              <Route path="billing/promo-codes" element={<PromoCodes />} />

              {/* System */}
              <Route path="system/server-monitor" element={<ServerMonitor />} />
              <Route path="system/telecom" element={<AsteriskCluster />} />
              <Route path="system/reviews" element={<ReviewManagement />} />

              {/* Platform */}
              <Route path="platform" element={<PlatformDashboard />} />
              <Route path="platform/organizations/:orgId" element={<OrganizationDetail />} />
              <Route path="platform/users" element={<UserManagement />} />
              <Route path="platform/audit-logs" element={<AuditLogViewer />} />

              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
          )}
        </Routes>
      </Suspense>
    </>
  );
}
